import React, { useEffect, useState, useCallback } from 'react';
import { getPositions, getRuleSet } from './api';
import AddTradeForm from './AddTradeForm';
import ManagePositionModal from './ManagePositionModal';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(val, decimals = 0) {
  if (val == null) return '—';
  return '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function statusBadge(status) {
  const map = {
    OPEN_PUT: { label: 'Open put', cls: 'badge-put' },
    OPEN_CALL: { label: 'Open call', cls: 'badge-call' },
    ASSIGNED: { label: 'Assigned', cls: 'badge-assigned' },
    CLOSED: { label: 'Closed', cls: 'badge-closed' },
    EXPIRED: { label: 'Expired', cls: 'badge-expired' },
  };
  const s = map[status] || { label: status, cls: 'badge-closed' };
  return <span className={'badge ' + s.cls}>{s.label}</span>;
}

function regimeBadge(flag) {
  if (!flag) return '—';
  const labels = { UPTREND: 'Uptrend', SIDEWAYS: 'Sideways', DOWNTREND: 'Downtrend', VOLATILE: 'Volatile' };
  return <span className={'regime regime-' + flag}>{labels[flag] || flag}</span>;
}

function getFirstPutLeg(legs) {
  if (!legs || legs.length === 0) return null;
  return legs.find(l => l.leg_type === 'SHORT_PUT') || legs[0];
}

function LegRow({ leg }) {
  const dte = leg.expiration_date
    ? Math.max(0, Math.ceil((new Date(leg.expiration_date).setHours(16,0,0,0) - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const inHarvest = dte !== null && dte <= 10 && dte > 0 && leg.outcome === 'OPEN';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 16, padding: '8px 16px 8px 40px', background: '#0f1520', borderBottom: '0.5px solid #1e2d3d', fontSize: 13, color: '#94a3b8' }}>
      <div>
        <span style={{ color: '#cbd5e1' }}>{leg.leg_type === 'SHORT_PUT' ? 'Short put' : 'Short call'}</span>
        <span style={{ marginLeft: 8, color: '#475569' }}>${parseFloat(leg.strike).toFixed(2)} · {formatDate(leg.expiration_date)}</span>
        {inHarvest && <span style={{ marginLeft: 8, fontSize: 11, color: '#fbbf24', fontWeight: 600 }}>⚡ Harvest</span>}
      </div>
      <div>{leg.contracts} contract{leg.contracts !== 1 ? 's' : ''}</div>
      <div>${parseFloat(leg.premium_actual || 0).toFixed(2)}/contract</div>
      <div>{dte !== null ? dte + ' DTE' : '—'}</div>
      <div style={{ color: leg.outcome === 'OPEN' ? '#4ade80' : '#475569' }}>{leg.outcome}</div>
    </div>
  );
}

export default function PositionLedger({ onSelectPosition }) {
  const [positions, setPositions] = useState([]);
  const [ruleSet, setRuleSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [managingPosition, setManagingPosition] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getPositions(), getRuleSet()])
      .then(([posRes, rsRes]) => { setPositions(posRes.data); setRuleSet(rsRes.data); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const now = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const totalDeployed = positions.reduce((sum, p) => sum + parseFloat(p.total_capital_at_risk || 0), 0);
  const totalPremium = positions.reduce((sum, p) => sum + parseFloat(p.total_premium_collected || 0), 0);

  if (loading) return <p className="loading">Loading positions...</p>;
  if (error) return <p className="error">Error: {error}</p>;

  return (
    <>
      <div className="topbar">
        <span className="page-title">Position ledger</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span className="timestamp">{now}</span>
          <button className="btn-add-trade" onClick={() => setShowForm(true)}>+ Add trade</button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Deployed capital</div>
          <div className="stat-value">{formatCurrency(totalDeployed)}</div>
          {ruleSet && <div className="stat-sub">{Math.round((totalDeployed / (parseFloat(ruleSet.max_total_deployed_pct) * 100000)) * 100)}% of max</div>}
        </div>
        <div className="stat-card">
          <div className="stat-label">Premium collected</div>
          <div className="stat-value">{formatCurrency(totalPremium)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open positions</div>
          <div className="stat-value">{positions.length}</div>
          {ruleSet && <div className="stat-sub">{ruleSet.max_concurrent_wheels} max</div>}
        </div>
        <div className="stat-card">
          <div className="stat-label">Watchlist</div>
          <div className="stat-value" style={{ fontSize: 14, paddingTop: 4 }}>{ruleSet ? ruleSet.watchlist.join(', ') : '—'}</div>
        </div>
      </div>

      <div className="section-label">Open positions</div>

      {positions.length === 0 ? (
        <p className="empty">No open positions. Press + Add trade to log your first position.</p>
      ) : (
        <>
          <div className="table-header">
            <span className="th">Position</span>
            <span className="th">Details</span>
            <span className="th">Premium</span>
            <span className="th">Time in trade</span>
            <span className="th">Regime</span>
            <span className="th"></span>
          </div>

          {positions.map(p => {
            const leg = getFirstPutLeg(p.legs);
            const expDate = leg ? formatDate(leg.expiration_date) : '—';
            const strike = leg ? '$' + parseFloat(leg.strike).toFixed(2) : '—';
            const contracts = leg ? leg.contracts : '—';
            const delta = leg ? leg.delta_at_open : null;
            const dte = leg && leg.expiration_date
              ? Math.max(0, Math.ceil((new Date(leg.expiration_date).setHours(16,0,0,0) - Date.now()) / (1000 * 60 * 60 * 24)))
              : null;
            const inHarvestZone = dte !== null && dte <= 10 && dte > 0;
            const openLegs = p.legs ? p.legs.filter(l => l.outcome === 'OPEN') : [];
            const isExpanded = expanded[p.position_id];

            return (
              <div key={p.position_id} style={{ marginBottom: 2 }}>
                <div className="position-row" onClick={() => toggleExpand(p.position_id)} style={{ cursor: 'pointer' }}>
                  <div>
                    <div className="ticker">{p.ticker}</div>
                    {statusBadge(p.status)}
                  </div>
                  <div>
                    {p.status === 'ASSIGNED' ? (
                      <>
                        <div className="cell-primary">{formatCurrency(p.cost_basis_per_share, 2)} cost basis</div>
                        <div className="cell-secondary">{p.shares_held} shares held</div>
                      </>
                    ) : (
                      <>
                        <div className="cell-primary">{strike} strike · {expDate}</div>
                        <div className="cell-secondary">{contracts} contract{contracts !== 1 ? 's' : ''}{delta ? ' · delta ' + delta : ''}</div>
                      </>
                    )}
                  </div>
                  <div>
                    <div className="premium">{formatCurrency(p.total_premium_collected)} collected</div>
                    {leg && leg.premium_actual && <div className="cell-secondary">${parseFloat(leg.premium_actual).toFixed(2)} / contract</div>}
                  </div>
                  <div>
                    <div className="cell-primary">{p.days_in_trade ?? '—'} days</div>
                    {dte !== null && <div className="cell-secondary">{dte} DTE remaining</div>}
                    {inHarvestZone && <div style={{ fontSize: 11, fontWeight: 600, color: '#fbbf24', marginTop: 3 }}>⚡ Harvest zone</div>}
                  </div>
                  <div>{regimeBadge(p.current_regime_flag)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
                    {openLegs.length > 0 && (
                      <span style={{ fontSize: 12, color: '#475569', cursor: 'pointer' }} onClick={() => toggleExpand(p.position_id)}>
                        {openLegs.length} leg{openLegs.length !== 1 ? 's' : ''} {isExpanded ? '▲' : '▼'}
                      </span>
                    )}
                    <span
                      className="view-link"
                      onClick={() => setManagingPosition(p)}
                      style={{ color: '#fbbf24' }}
                    >
                      Manage →
                    </span>
                    <span className="view-link" onClick={() => onSelectPosition(p.position_id)}>View →</span>
                  </div>
                </div>
                {isExpanded && openLegs.length > 0 && (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 16, padding: '6px 16px 6px 40px', background: '#0a0f18', fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <span>Contract</span><span>Size</span><span>Premium</span><span>DTE</span><span>Status</span>
                    </div>
                    {openLegs.map(l => <LegRow key={l.leg_id} leg={l} />)}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {showForm && <AddTradeForm onClose={() => setShowForm(false)} onSaved={load} />}
      {managingPosition && (
        <ManagePositionModal
          position={managingPosition}
          onClose={() => setManagingPosition(null)}
          onSaved={load}
        />
      )}
    </>
  );
}
